import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { TrendingDown, Trash2, CheckCircle } from 'lucide-react';
import type { PriceTierTableData, PriceTierItem, PriceTier } from '@/types/purchaseFlow';
import type { ItemUpdate } from './RichBlockRenderer';

interface PriceTierTableBlockProps {
  data: unknown;
  onUpdateItems?: (updates: ItemUpdate[], removals: string[]) => void;
}

/** Find the active tier for a given qty */
function activeTier(tiers: PriceTier[], qty: number): PriceTier | undefined {
  let best: PriceTier | undefined;
  for (const t of tiers) {
    if (qty >= t.min_qty) best = t;
  }
  return best;
}

/** Find the next cheaper tier above current qty (if any) */
function nextTier(tiers: PriceTier[], qty: number): PriceTier | undefined {
  for (const t of tiers) {
    if (t.min_qty > qty) return t;
  }
  return undefined;
}

const PriceTierTableBlock: React.FC<PriceTierTableBlockProps> = ({ data, onUpdateItems }) => {
  const { t } = useTranslation('purchaseflow');
  const parsed = data as PriceTierTableData;
  if (!parsed?.items?.length) return null;

  const [quantities, setQuantities] = useState<Record<string, number>>(() => {
    const init: Record<string, number> = {};
    for (const item of parsed.items) {
      init[item.part_code] = item.current_qty;
    }
    return init;
  });
  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const [applied, setApplied] = useState(false);

  // Reset state when data changes
  useEffect(() => {
    const init: Record<string, number> = {};
    for (const item of parsed.items) {
      init[item.part_code] = item.current_qty;
    }
    setQuantities(init);
    setRemoved(new Set());
    setApplied(false);
  }, [parsed]);

  const setQty = useCallback((partCode: string, qty: number) => {
    if (qty > 0) setQuantities(prev => ({ ...prev, [partCode]: qty }));
  }, []);

  const toggleRemove = useCallback((partCode: string) => {
    setRemoved(prev => {
      const next = new Set(prev);
      if (next.has(partCode)) next.delete(partCode);
      else next.add(partCode);
      return next;
    });
  }, []);

  const activeItems = parsed.items.filter(i => !removed.has(i.part_code));

  const computeTotal = (item: PriceTierItem, qty: number) => {
    const tier = activeTier(item.tiers, qty);
    return (tier?.unit_price ?? item.current_price) * qty;
  };

  const originalGrandTotal = parsed.items.reduce((s, i) => s + i.current_price * i.current_qty, 0);
  const newGrandTotal = activeItems.reduce((s, i) => s + computeTotal(i, quantities[i.part_code] ?? i.current_qty), 0);
  const diff = newGrandTotal - originalGrandTotal;

  const hasChanges = activeItems.length !== parsed.items.length ||
    parsed.items.some(i => !removed.has(i.part_code) && quantities[i.part_code] !== i.current_qty);

  const handleApply = () => {
    console.log('[PriceTierTable] === APPLY CLICKED ===');
    const updates: ItemUpdate[] = [];
    const removals: string[] = [];
    for (const item of parsed.items) {
      if (removed.has(item.part_code)) {
        removals.push(item.part_code);
      } else {
        const qty = quantities[item.part_code] ?? item.current_qty;
        const tier = activeTier(item.tiers, qty);
        const price = tier?.unit_price ?? item.current_price;
        updates.push({ part_code: item.part_code, quantity: qty, unit_price: price });
      }
    }
    console.log('[PriceTierTable] handleApply', { updates, removals, hasCallback: !!onUpdateItems });
    onUpdateItems?.(updates, removals);
    setApplied(true);
  };

  // Click a tier cell → set qty to that tier's min_qty
  const handleTierClick = (partCode: string, tierMinQty: number) => {
    console.log('[PriceTierTable] tier clicked', { partCode, tierMinQty });
    if (removed.has(partCode)) return;
    setQty(partCode, tierMinQty);
  };

  // ── Collapsed state after apply ──
  if (applied) {
    const appliedCount = activeItems.length;
    const currency = parsed.items[0]?.currency ?? 'TWD';
    return (
      <div className="mt-2 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-2 flex items-center gap-2 text-xs">
        <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
        <span className="text-emerald-700 dark:text-emerald-400 font-medium">
          {t('richBlocks.tierApplied', { count: appliedCount, total: `${currency} ${newGrandTotal.toLocaleString()}` })}
        </span>
        {diff !== 0 && (
          <span className={`font-mono text-[10px] ml-1 ${diff < 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`}>
            ({diff > 0 ? '+' : ''}{diff.toLocaleString()})
          </span>
        )}
      </div>
    );
  }

  // ── Expanded interactive table ──
  return (
    <div className="mt-2 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="px-3 py-2 bg-blue-50 dark:bg-blue-900/20 flex items-center gap-2 text-xs font-medium text-blue-700 dark:text-blue-400">
        <TrendingDown className="w-3.5 h-3.5" />
        {t('richBlocks.priceTiers', { vendor: parsed.vendor_name })}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
              <th className="text-left px-3 py-1.5 font-medium">{t('richBlocks.tierCol.item')}</th>
              <th className="text-center px-2 py-1.5 font-medium">{t('richBlocks.tierCol.qty')}</th>
              {parsed.items[0]?.tiers.map(tier => (
                <th key={tier.min_qty} className="text-right px-2 py-1.5 font-medium whitespace-nowrap">
                  {tier.min_qty >= 1000 ? `${(tier.min_qty / 1000)}k` : tier.min_qty}+
                </th>
              ))}
              <th className="text-right px-3 py-1.5 font-medium">{t('richBlocks.tierCol.amount')}</th>
              <th className="w-8 px-1 py-1.5"></th>
            </tr>
          </thead>
          <tbody>
            {parsed.items.map((item) => {
              const isRemoved = removed.has(item.part_code);
              const qty = quantities[item.part_code] ?? item.current_qty;
              const active = activeTier(item.tiers, qty);
              const next = nextTier(item.tiers, qty);
              const lineTotal = computeTotal(item, qty);
              const originalTotal = item.current_price * item.current_qty;
              const lineDiff = lineTotal - originalTotal;

              return (
                <tr key={item.part_code} className={`border-t border-gray-100 dark:border-gray-700 ${
                  isRemoved ? 'opacity-30 line-through' : ''
                }`}>
                  {/* Item name + spec */}
                  <td className="px-3 py-2">
                    <div className="font-medium text-gray-700 dark:text-gray-300 truncate max-w-[140px]">{item.product_name}</div>
                    <div className="text-[10px] text-gray-400 truncate max-w-[140px]">{item.specification}</div>
                  </td>

                  {/* Qty input */}
                  <td className="px-2 py-2 text-center">
                    {isRemoved ? (
                      <span className="font-mono text-gray-400">{qty.toLocaleString()}</span>
                    ) : (
                      <input
                        type="number"
                        min={1}
                        value={qty}
                        onChange={e => setQty(item.part_code, parseInt(e.target.value, 10) || 1)}
                        className="w-[72px] px-1.5 py-1 rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-center font-mono text-xs focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                      />
                    )}
                  </td>

                  {/* Tier price cells — clickable to select tier */}
                  {item.tiers.map(tier => {
                    const isActive = active && tier.min_qty === active.min_qty;
                    const isNext = next && tier.min_qty === next.min_qty;
                    return (
                      <td
                        key={tier.min_qty}
                        onClick={() => handleTierClick(item.part_code, tier.min_qty)}
                        className={`px-2 py-2 text-right font-mono whitespace-nowrap transition-colors ${
                          isRemoved ? '' : 'cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20'
                        } ${
                          isActive
                            ? 'text-blue-700 dark:text-blue-400 font-semibold bg-blue-50 dark:bg-blue-900/10'
                            : isNext
                            ? 'text-emerald-600 dark:text-emerald-400'
                            : 'text-gray-400 dark:text-gray-500'
                        }`}
                        title={isRemoved ? '' : `Set qty to ${tier.min_qty.toLocaleString()}`}
                      >
                        {tier.unit_price.toFixed(tier.unit_price < 1 ? 2 : 0)}
                        {isActive && !isRemoved && (
                          <span className="ml-0.5 text-blue-500 dark:text-blue-400">✓</span>
                        )}
                      </td>
                    );
                  })}

                  {/* Line total + diff */}
                  <td className="px-3 py-2 text-right">
                    <div className="font-mono font-medium text-gray-700 dark:text-gray-300">
                      {isRemoved ? '—' : lineTotal.toLocaleString()}
                    </div>
                    {!isRemoved && lineDiff !== 0 && (
                      <div className={`text-[10px] font-mono ${lineDiff < 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`}>
                        {lineDiff > 0 ? '+' : ''}{lineDiff.toLocaleString()}
                      </div>
                    )}
                  </td>

                  {/* Remove toggle */}
                  <td className="px-1 py-2 text-center">
                    {parsed.items.length > 1 && (
                      <button
                        onClick={() => toggleRemove(item.part_code)}
                        className={`p-1 rounded transition-colors ${
                          isRemoved
                            ? 'text-blue-500 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/30'
                            : 'text-gray-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20'
                        }`}
                        title={isRemoved ? t('richBlocks.tierUndo') : t('richBlocks.tierRemove')}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>

          {/* Footer totals */}
          <tfoot>
            <tr className="border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800">
              <td className="px-3 py-2 font-medium text-gray-700 dark:text-gray-300" colSpan={2 + (parsed.items[0]?.tiers.length ?? 0)}>
                {t('richBlocks.tierTotal')}
              </td>
              <td className="px-3 py-2 text-right">
                <div className="font-mono font-semibold text-gray-800 dark:text-gray-200">
                  {parsed.items[0]?.currency ?? 'TWD'} {newGrandTotal.toLocaleString()}
                </div>
                {diff !== 0 && (
                  <div className={`text-[10px] font-mono ${diff < 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`}>
                    {diff > 0 ? '+' : ''}{diff.toLocaleString()} vs original
                  </div>
                )}
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Action button */}
      <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-2">
        <button
          onClick={handleApply}
          disabled={!hasChanges}
          className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
            hasChanges
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-200 text-gray-400 dark:bg-gray-700 dark:text-gray-500 cursor-not-allowed'
          }`}
        >
          {t('richBlocks.tierApplyChanges')}
        </button>
      </div>
    </div>
  );
};

export default PriceTierTableBlock;
